<template>
	<Row class="page-container-row">
		<Col :span="24">
			<PageContainer>
				<Row :gutter="[16, 16]">
					<Col :span="24" :xl="{ span: 8 }">
						<BackButtonHeader :header="$t('str-import.header')" />
						<Spin :spinning="isLoading">
							<Card :title="$t('str-import.formHeader')">
								<Form
									:model="formState"
									:label-col="{ span: 8 }"
									:wrapper-col="{ span: 16 }"
									:colon="false"
								>
									<FormItem
										:label="$t('str-import.fields.host')"
										v-bind="validateInfos.host"
									>
										<InputRestricted
											ref="hostInput"
											v-model="formState.host"
											:allowedFormatChecker="hostnameChecker"
											:toolTipText="$t('validators.hostnameMessage')"
											@keyup.enter="onSubmit"
										/>
									</FormItem>
									<FormItem
										:label="$t('str-import.fields.port')"
										v-bind="validateInfos.port"
									>
										<InputDecimal v-model="formState.port" :allowPoint="false" range="port" @keyup.enter="onSubmit" />
									</FormItem>

									<FormItem
										:label="$t('str-import.fields.username')"
										v-bind="validateInfos.username"
									>
										<InputRestricted
											v-model="formState.username"
											:allowedFormatChecker="asciiNoColonChecker"
											:toolTipText="$t('validators.asciiNoColonMessage')"
											@keyup.enter="onSubmit"
										/>
									</FormItem>
									<FormItem
										:label="$t('str-import.fields.password')"
										v-bind="validateInfos.password"
									>
										<Input v-model:value="formState.password" @keyup.enter="onSubmit" />
									</FormItem>
								</Form>
								<template #actions>
									<Button type="primary" @click.prevent="onSubmit" :loading="isLoading">{{ $t('str-import.submitButtonLabel') }}</Button>
								</template>
							</Card>
						</Spin>
					</Col>
					<Col v-if="records.length > 0" :span="24" :xl="{ span: 16 }">
						<TypographyTitle :level="3" class="pd-md-x">{{ $t('str-import.resultsHeader') }}</TypographyTitle>
						<VerticalLayout>
							<Table
								size="small"
								:columns="columns"
								:data-source="records"
								:loading="isLoading"
								:pagination="false"
								:showSorterTooltip="false"
								bordered
							>
								<template #bodyCell="{ column, record }">
									<template v-if="column.dataIndex === 'store'">
										<div v-if="['error', 'update'].includes(record.status)">
											{{ $t('str-import.messages.' + record['error_message']) }}
										</div>
										<LabeledSwitch
											v-if="['ok', 'update'].includes(record.status)"
											:class="record.status === 'update' ? 'import-switch-padding' : ''"
											v-model="record.store"
											:rightLabel="$t('str-import.importLabel')"
											:small="true"
										/>
									</template>
									<template v-else-if="column.dataIndex === 'mntpnt_int'">
										<InputRestricted
											v-model="record['mntpnt_int']"
											:maxlength="MNTPNT_INT_MAX_LENGTH"
											:allowedFormatChecker="mountpointChecker"
											:toolTipText="$t('validators.mntpntIntMessage')"
										/>
										<div v-if="record.status === 'update'">
											{{ $t('str-import.messages.update') }}
										</div>
									</template>
								</template>
							</Table>
							<div class="flex-wrapper flex-spacing">
								<Button type="primary" @click.prevent="onImport" :loading="isLoading" :disabled="selectedRecords.length === 0">
									{{ $t('str-import.importButtonLabel', [selectedRecords.length, records.length]) }}
								</Button>
								<Button :loading="isLoading" @click="selectAll">{{ $t('str-import.selectAllButtonLabel') }}</Button>
							</div>
						</VerticalLayout>
					</Col>
				</Row>
			</PageContainer>
		</Col>
	</Row>
</template>

<script>
import { ref, reactive, computed, toRaw, onMounted } from 'vue';
import {
	TypographyTitle, Table, Button, Row, Col, Spin, Card, Form, FormItem, Input, message
} from 'ant-design-vue';
import { useI18n } from 'vue-i18n'

import camelCase from 'lodash/camelCase';

import { useBaseStationsStore } from '@/stores/baseStations';

import PageContainer from '@/components/common/PageContainer';

import InputDecimal from '@/components/common/InputDecimal';
import InputRestricted from '@/components/common/InputRestricted';
import LabeledSwitch from '@/components/common/LabeledSwitch.vue';
import VerticalLayout from '@/components/common/VerticalLayout';
import BackButtonHeader from '@/components/common/BackButtonHeader';

import { showErrorMessage } from '@/components/common/errorMessage';

import Api from '@/api/Api';
import Consts from '@/api/Consts';
import Utils from '@/api/Utils';
import Validators from '@/api/Validators';
import TranslationUtils from '@/i18n/TranslationUtils';


const sorter = field => (a, b) => {
	const valueA = a[field];
	const valueB = b[field];
	if (valueA != null && valueB != null) {
		return valueA - valueB;
	}
	if (valueA == null && valueB != null) {
		return -1;
	}
	if (valueA != null && valueB == null) {
		return 1;
	}
	return a['mntpnt_int'].localeCompare(b['mntpnt_int']);
}


const COLUMNS = [
	{
		dataIndex: 'mountpoint',
		children: [
			{
				dataIndex: 'mntpnt_int',
				sorter: {
					compare: Utils.objectFieldMultiSorter('mntpnt_int'),
				},
			},
			{
				dataIndex: 'mntpnt_src',
				sorter: {
					compare: Utils.objectFieldMultiSorter('mntpnt_src'),
				},
			},
		],
	},
	{
		dataIndex: 'location',
		sorter: {
			compare: Utils.objectFieldMultiSorter('location'),
		},
	},
	{
		dataIndex: 'latitudeStr',
		align: 'right',
		sorter: {
			compare: sorter('latitude'),
		},
	},
	{
		dataIndex: 'longitudeStr',
		align: 'right',
		sorter: {
			compare: sorter('longitude'),
		},
	},
	{ dataIndex: 'store' },
];


export default {
	components: {
		TypographyTitle, Button, Table, Row, Col, Spin, Card, Form, FormItem, Input,
		PageContainer, InputDecimal, InputRestricted, VerticalLayout, LabeledSwitch, BackButtonHeader,
	},
	setup() {
		const { t } = useI18n();
		const isLoading = ref(false);
		const formState = reactive({
			host: '',
			port: null,
			username: '',
			password: '',
		});
		const credentials = reactive({
			host: '',
			port: null,
			username: '',
			password: '',
		});
		const isSecure = ref(false);
		const columns = TranslationUtils.addTranslatedField(
			COLUMNS,
			'dataIndex', 'title',
			'str-import.tableColumns',
		);
		const records = ref([]);
		const {
			validate,
			validateInfos,
		} = Validators.useStrImportFormRules(formState);
		const onSubmit = () => {
			validate().then(() => {
				message.info(t('str-import.messages.search'));
				isLoading.value = true;
				Api.baseStationStrImportSource(toRaw(formState), response => {
					if (response.status === 'ok') {
						records.value = response.records.map(record => ({
							...record,
							latitudeStr: Utils.formatLatLong(record.latitude),
							longitudeStr: Utils.formatLatLong(record.longitude),
							store: false,
						}));
						isSecure.value = response.secure;
						Object.assign(credentials, toRaw(formState));
						if (response.records.length === 0) {
							message.error(t('str-import.messages.notFound'));
						}
					} else if (response.status === 'noAccess') {
						message.error(t('str-import.messages.noAccess'));
					} else {
						['host', 'port', 'username', 'password'].forEach(element => {
							if (response.errors[element]) {
								let jsElement = camelCase(element);
								validateInfos[jsElement].validateStatus = 'error';
								validateInfos[jsElement].help = response.errors[element];
								delete response.errors[element];
							}
						});
						showErrorMessage(response.errors);
					}
					isLoading.value = false;
				});
			});
		};
		const selectedRecords = computed(() => records.value.filter(record => record.store));
		const onImport = () => {
			isLoading.value = true;
			Api.baseStationStrImportMountpoints(selectedRecords.value, isSecure.value, toRaw(credentials), response => {
				if (response.status === 'ok') {
					records.value = [];
					message.success(t('str-import.messages.success'));
				} else {
					showErrorMessage(response.errors);
				}
				useBaseStationsStore().baseStationRefreshList();
				isLoading.value = false;
			});
		};
		const selectAll = () => records.value.filter(record => !record['error_message']).map(record => record.store = true);
		const hostInput = ref(null);
		onMounted(() => hostInput.value.focus());
		return {
			isLoading,
			formState,
			columns,
			validateInfos,
			records,
			selectedRecords,
			onSubmit,
			onImport,
			selectAll,
			hostInput,
			asciiNoColonChecker: Validators.asciiNoColonChecker,
			hostnameChecker: Validators.hostnameChecker,
			mountpointChecker: Validators.mountpointChecker,
			MNTPNT_INT_MAX_LENGTH: Validators.MNTPNT_INT_MAX_LENGTH,
			colorBlack: Consts.COLORS.dark0,
		};
	},
}
</script>

<style scoped>
.flex-spacing {
	justify-content: space-between;
}

.import-switch-padding {
	margin-top: 10px;
}
</style>
